import { voiceErrorKey } from '@/features/voice/voice-contract';
import * as React from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import {
  MessageSquare,
  X,
  Search,
  Hash,
  Volume2,
  Phone,
  Mic,
  MicOff,
  Headphones,
  HeadphoneOff,
  Maximize2,
  PhoneOff,
  Loader2,
  BellOff,
  Video,
  Check,
  Users,
  ArrowUpRight,
  ChevronRight,
} from 'lucide-react';
import { teamChatQueries, useCreateChannel, type Channel, type DirectoryUser } from '../queries';
import { ChatCreationStatus } from './ChatCreationStatus';
import { localizedErrorMessage } from '@/lib/i18n/errors';
import { dmCounterpart, dmDisplayName } from '../dm-display';
import { effectiveNotifyPref, getChannelNotifyPref } from '../channel-notify-prefs';
import { colorFromName, initialsFromName } from '@/components/ui-kit';
import { useChatDockStore } from '../chat-dock-store';
import { useAuthStore } from '@/store/auth-store';
import { useVoiceSessionStore } from '@/features/voice/voice-session-store';
import { useVoiceOccupancy, useVoiceObservation } from '@/features/voice/voice-presence-store';
import { voiceQueries } from '@/features/voice/queries';
import { useVoiceRoomStore, type VoiceRoomControls } from '@/features/voice/voice-room-store';
import {
  buildVoicePillView,
  shouldShowVoiceStatusPill,
  type VoicePillView,
} from '@/features/voice/voice-pill-view';
import { callQueries, useStartCall, type AgentSummary } from '@/features/calls/queries';
import { Avatar } from '@/components/ui/Avatar';
import { usePresence } from '@/lib/presence';
import { cn } from '@/lib/utils/cn';
import { DockChatWindow } from './DockChatWindow';
import { TeamChatLiveAlerts } from './TeamChatLiveAlerts';
import { useTranslation } from '@/lib/i18n/react';
import { formatNumber } from '@/lib/i18n/runtime';
import { commsText,cappedCount,formatCommsTime } from '../alert-copy';
import { sessionScope,sessionRevision,requireCurrentSession } from '@/features/auth/session-scope';

type HubTab = 'chat' | 'voice' | 'calls';

/**
 * Unified communication hub (mounted app-wide in providers, outside routes).
 *
 * ONE bottom-right launcher for ALL real-time comms — team chat, voice channels,
 * and calls — replacing the separate chat bubble + (launcher-less) voice/calls.
 * It only *launches*; the underlying machinery is untouched:
 *   - Chat   → `chat-dock-store` opens `DockChatWindow`s (rendered here).
 *   - Voice  → `voice-session-store.join()`; the global `VoiceDock` renders the
 *              actual room/pill (this just picks which channel to join).
 *   - Calls  → the hub owns the inline voice/video picker; `CallOverlay`
 *              (also global) handles ringing + the in-call room.
 *
 * The hub is also the SINGLE renderer of the floating voice pill (TC-33):
 * connecting / reconnecting / error status AND the live minimized pill/bolitas
 * all read the shared voice-room store — VoiceRoom only publishes state and
 * keeps the audio sinks alive while non-expanded.
 */

function clearReservation() {
  document.documentElement.style.removeProperty('--comms-hub-reserved-height');
  document.documentElement.style.removeProperty('--comms-hub-reserved-width');
}

export function CommsHub() {
  const scope=useAuthStore(sessionScope),revision=useAuthStore(()=>sessionRevision());
  return <OwnedCommsHub key={`${scope}:${revision}`}/>;
}
function OwnedCommsHub() {
  const {t}=useTranslation();
  const token = useAuthStore((s) => s.token);
  const currentUserId = useAuthStore((s) => s.user?.id ?? null);
  const openChats = useChatDockStore((s) => s.openChats);
  const minimized = useChatDockStore((s) => s.minimized);
  const openChat = useChatDockStore((s) => s.openChat);
  const closeChat = useChatDockStore((s) => s.closeChat);
  const toggleMinimized = useChatDockStore((s) => s.toggleMinimized);

  // TC-21: only non-minimized chats render as full windows; the rest collapse
  // to 48px chips stacked next to the launcher (Messenger chat-heads pattern).
  const expandedChats = openChats.filter((id) => !minimized.includes(id));
  const minimizedChats = openChats.filter((id) => minimized.includes(id));

  const [open, setOpen] = React.useState(false);
  const [tab, setTab] = React.useState<HubTab>('chat');
  const launcher=React.useRef<HTMLButtonElement>(null);
  const closeHub=()=>{setOpen(false);launcher.current?.focus();};
  const launcherColumn = React.useRef<HTMLDivElement>(null);
  // Reserve only the passive stack. Opening the hub is an intentional overlay;
  // full dock windows are siblings and do not consume the route's viewport.
  React.useLayoutEffect(() => {
    if (!token) { clearReservation(); return; }
    if (open) return;
    const element = launcherColumn.current;
    if (!element) return;
    // Height feeds block reservations (phones, dense routes, the customer
    // rail's scroll padding); width lets the Inbox keep its composer and thread
    // beside the stack instead of leaving an empty band below them.
    const measure = () => {
      const rect = element.getBoundingClientRect();
      document.documentElement.style.setProperty('--comms-hub-reserved-height', `${Math.ceil(rect.height) + 32}px`);
      document.documentElement.style.setProperty('--comms-hub-reserved-width', `${Math.ceil(rect.width) + 32}px`);
    };
    measure();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    observer?.observe(element);
    window.addEventListener('resize', measure);
    return () => { observer?.disconnect(); window.removeEventListener('resize', measure); };
  }, [token, open]);
  React.useEffect(() => clearReservation, []);

  // Providers owns the hub outside RouterProvider, so read the URL at render
  // time. Opening the panel changes local state and always refreshes this value.
  const isFullChatRoute =
    typeof window !== 'undefined' && window.location.pathname.startsWith('/chat');

  const channelsQuery = useQuery({ ...teamChatQueries.channels(), enabled: !!token });
  const channels=channelsQuery.data;

  // Live voice-session state (published by VoiceRoom into the shared store) so
  // the hub can show the minimized pill / mute bolitas without owning the room.
  const voiceChannelId = useVoiceSessionStore((s) => s.channelId);
  const voicePhase = useVoiceSessionStore((s) => s.phase);
  const voiceMinimized = useVoiceSessionStore((s) => s.minimized);
  const vState = useVoiceRoomStore((s) => s.connectionState);
  const vError = useVoiceRoomStore((s) => s.errorMessage);
  const vMic = useVoiceRoomStore((s) => s.micEnabled);
  const vDeaf = useVoiceRoomStore((s) => s.deafened);
  const vTotal = useVoiceRoomStore((s) => s.total);
  const vName = useVoiceRoomStore((s) => s.channelName);
  const vControls = useVoiceRoomStore((s) => s.controls);

  // TC-33: the hub is the ONLY renderer of the floating voice pill — VoiceRoom
  // just publishes its connection state into the shared store. Transitional
  // states (connecting / error always; reconnecting while minimized — expanded
  // shows its own banner) render a status pill in this same bottom-right
  // cluster, so the pill never jumps position when the connection comes up.
  const voiceSessionUp = !!voiceChannelId && voicePhase === 'live';
  const voiceStatusView: VoicePillView | null =
    voiceSessionUp && shouldShowVoiceStatusPill(vState, voiceMinimized)
      ? buildVoicePillView(vState, { channelName: vName, errorMessage: vError })
      : null;

  // Compact LIVE voice UI (full pill when the hub is open, bolitas when
  // closed) — only when the room is live AND minimized (expanded → the full
  // VoiceRoom overlay is up).
  const inVoice = voiceSessionUp && vState === 'live' && voiceMinimized;

  const byId = React.useMemo(() => {
    const m = new Map<string, Channel>();
    for (const c of channels ?? []) m.set(c.id, c);
    return m;
  }, [channels]);

  // Total unread across every channel/DM → launcher badge (same source the
  // HubRow badges use, so they always agree).
  const totalUnread = React.useMemo(
    () => (channels ?? []).reduce((n, c) => n + (c.unread_count ?? 0), 0),
    [channels],
  );

  if (!token) return null;

  return (
    <div className="comms-hub-root fixed bottom-4 end-4 z-[170] flex items-end gap-3">
      {/* Open (expanded) conversation windows (left of the launcher) */}
      {expandedChats.map((id) => {
        const ch = byId.get(id);
        const isDirect = ch?.type === 'direct';
        // DMs: the stored channel.name is fixed by the CREATOR (often the
        // recipient's own name) — derive the label from the counterpart instead.
        const channelName = ch
          ? isDirect
            ? dmDisplayName(ch, currentUserId)
            : ch.name
          : t('comms.conversation');
        return (
          <DockChatWindow
            key={id}
            channelId={id}
            channelName={channelName}
            isDirect={isDirect}
            onClose={() => closeChat(id)}
            onMinimize={() => toggleMinimized(id)}
          />
        );
      })}

      {/* Launcher column — voice pill (status or live) / panel stack above; bolitas + bubble below. */}
      <div ref={launcherColumn} className="comms-hub-launcher-column relative flex flex-col items-end">
        {voiceStatusView && (
          <VoiceStatusPill
            view={voiceStatusView}
            errorKey={voiceErrorKey(vError)}
            // Error pill's close ends the session: VoiceDock unmounts the room,
            // which disconnects and resets the shared store. (No registered
            // controls exist in the error state — the room never connected.)
            onClose={() => useVoiceSessionStore.getState().leave()}
          />
        )}
        {!voiceStatusView && open && inVoice && (
          <VoicePill name={vName} total={vTotal} micEnabled={vMic} deafened={vDeaf} controls={vControls} />
        )}
        {open && (
          <HubPanel
            channels={channels ?? []}
            channelState={channelsQuery.isError?'error':channelsQuery.isPending?'loading':'ready'}
            onRetryChannels={()=>void channelsQuery.refetch()}
            tab={tab}
            onTab={setTab}
            totalUnread={totalUnread}
            onClose={closeHub}
            onPickChat={(id) => {
              openChat(id);
              setOpen(false);
            }}
            onJoinVoice={(c) => {
              // TC-23: clicking the channel you're ALREADY live in must NOT
              // re-join (join() resets phase to 'lobby' → VoiceRoom unmounts →
              // disconnect). Expand the existing overlay instead; switching to
              // ANOTHER channel while live asks for confirmation first.
              const voice = useVoiceSessionStore.getState();
              if (voice.channelId === c.id && voice.phase === 'live') {
                voice.setMinimized(false);
                setOpen(false);
                return;
              }
              if (voice.channelId && voice.channelId !== c.id && voice.phase === 'live') {
                if (!window.confirm(t('comms.changeVoice'))) return;
              }
              voice.join(c.id, c.name);
              setOpen(false);
            }}
            onCallStarted={() => {
              setOpen(false);
            }}
            showOpenFullChat={!isFullChatRoute}
            onOpenFullChat={() => {
              setOpen(false);
              window.history.pushState(null, '', '/chat');
              window.dispatchEvent(new PopStateEvent('popstate'));
            }}
          />
        )}
        {/* Incoming internal-chat preview: part of this launcher stack so it
            sits directly above the communication bubble. The expanded hub
            already exposes the unread list, so avoid duplicating the card. */}
        {!open && (
          <TeamChatLiveAlerts
            channels={channels ?? []}
            currentUserId={currentUserId}
            totalUnread={totalUnread}
          />
        )}
        {/* Minimized chat chips — vertical stack right above the launcher.
            Click restores the window (TC-21). */}
        {minimizedChats.length > 0 && (
          <div className="mb-2 me-2 flex flex-col items-center gap-2">
            {minimizedChats.map((id) => {
              const ch = byId.get(id);
              const isDirect = ch?.type === 'direct';
              const name = ch
                ? isDirect
                  ? dmDisplayName(ch, currentUserId)
                  : ch.name
                : t('comms.conversation');
              return (
                <DockChip
                  key={id}
                  name={name}
                  avatar={isDirect && ch ? dmCounterpart(ch, currentUserId)?.user_avatar : undefined}
                  isDirect={isDirect}
                  unread={ch?.unread_count ?? 0}
                  onClick={() => toggleMinimized(id)}
                />
              );
            })}
          </div>
        )}
        <div className="flex items-center gap-2">
          {!open && inVoice && (
            <VoiceBolitas name={vName} total={vTotal} micEnabled={vMic} controls={vControls} />
          )}
          <button
            ref={launcher}
            type="button"
            onClick={() => setOpen((v) => !v)}
            title={t(open?'comms.closeHub':'comms.openHub')}
            aria-label={t(open?'comms.closeHub':'comms.openHub')}
            aria-expanded={open}
            className={cn(
              'comms-hub-launcher group relative flex h-14 items-center gap-2.5 rounded-md px-3 text-white shadow-[0_16px_40px_rgba(71,36,157,0.28)] transition-all hover:-translate-y-0.5 hover:shadow-[0_20px_44px_rgba(71,36,157,0.34)]',
              open ? 'bg-ink-900' : 'grad-blaze-diag',
            )}
          >
            <span className="flex h-9 w-9 items-center justify-center rounded-sm bg-white/15 ring-1 ring-white/15">
              {open ? <X size={19} /> : <MessageSquare size={19} />}
            </span>
            <span className="comms-hub-launcher-copy hidden pe-1 text-start sm:block">
              <span className="block text-[10px] font-bold uppercase tracking-[0.16em] text-white/65">
                {t('comms.team')}
              </span>
              <span className="block text-sm font-extrabold leading-tight">
                {t(open?'close':'comms.title')}
              </span>
            </span>
            {totalUnread > 0 && (
              <span className="absolute -end-1.5 -top-1.5 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-blaze-orange px-1.5 text-[10px] font-extrabold text-white shadow-lg ring-2 ring-white">
                {cappedCount(totalUnread)}
              </span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Minimized chat head (TC-21): 48px circular chip — counterpart avatar for DMs
 * or colored initials for channels — with the channel's unread badge. Clicking
 * it restores the full DockChatWindow (the store re-enforces the expanded cap,
 * collapsing the oldest other window if needed).
 */
function DockChip({
  name,
  avatar,
  isDirect,
  unread,
  onClick,
}: {
  name: string;
  avatar?: string;
  isDirect?: boolean;
  unread: number;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={`${isDirect ? '' : '#'}${name}`}
      className="relative h-12 w-12 rounded-full shadow-xl flex items-center justify-center text-white text-sm font-bold hover:scale-105 transition-transform"
      style={{ background: avatar ? 'var(--paper, #fff)' : colorFromName(name) }}
    >
      <span className="h-12 w-12 rounded-full overflow-hidden flex items-center justify-center ring-2 ring-white">
        {avatar ? (
          <img src={avatar} alt="" className="h-full w-full object-cover" />
        ) : (
          initialsFromName(name)
        )}
      </span>
      {unread > 0 && (
        <span className="absolute -top-1 -end-1 min-w-[18px] h-[18px] px-1 rounded-full bg-blaze-orange text-white text-[10px] font-bold flex items-center justify-center shadow ring-2 ring-white">
          {cappedCount(unread)}
        </span>
      )}
    </button>
  );
}

/**
 * Transitional voice pill (TC-33): connecting spinner, amber "Reconectando…",
 * or red error + close — rendered in the SAME bottom-right cluster as the live
 * pill/bolitas so the indicator never jumps position. VoiceRoom publishes the
 * state; this is the only place that paints it.
 */
function VoiceStatusPill({
  errorKey, view, onClose }: { view: VoicePillView;
  errorKey?: ReturnType<typeof voiceErrorKey>; onClose: () => void }) {
  const {t}=useTranslation();
  if (view.kind === 'live') return null;
  return (
    <div className="mb-3 w-[min(24rem,calc(100vw-2rem))] bg-ink-900/95 backdrop-blur rounded-2xl shadow-2xl border border-white/10 px-3 py-2.5 flex items-center gap-2.5">
      {view.kind === 'error' ? (
        <>
          <span className="h-9 w-9 rounded-xl bg-red-500/15 text-red-400 flex items-center justify-center flex-shrink-0">
            <PhoneOff size={16} />
          </span>
          <span className="flex-1 min-w-0 text-red-400 text-xs font-semibold truncate" title={t(errorKey?'voice.api.'+errorKey:'comms.voiceFailed')}>
            {t(errorKey?'voice.api.'+errorKey:'comms.voiceFailed')}
          </span>
          <button
            type="button"
            onClick={onClose}
            title={t('close')}
            className="h-11 w-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors flex-shrink-0"
          >
            <X size={15} />
          </button>
        </>
      ) : (
        <>
          <Loader2
            size={16}
            className={cn(
              'animate-spin flex-shrink-0',
              view.kind === 'reconnecting' ? 'text-amber-400' : 'text-blaze-green',
            )}
          />
          <span
            className={cn(
              'flex-1 min-w-0 text-sm truncate',
              view.kind === 'reconnecting' ? 'text-amber-300 font-semibold' : 'text-white/70',
            )}
          >
            {t(view.kind==='reconnecting'?'comms.reconnecting':'comms.connecting',{name:view.channelName||t('comms.voice')})}
          </span>
        </>
      )}
    </div>
  );
}

/**
 * Integrated voice pill shown ABOVE the open hub panel — full controls (mute,
 * deafen, expand, leave). Driven entirely by the shared voice-room store.
 */
function VoicePill({
  name,
  total,
  micEnabled,
  deafened,
  controls,
}: {
  name?: string;
  total: number;
  micEnabled: boolean;
  deafened: boolean;
  controls: VoiceRoomControls | null;
}) {
  const {t}=useTranslation();
  return (
    <div className="mb-3 w-[min(24rem,calc(100vw-2rem))] bg-ink-900/95 backdrop-blur rounded-2xl shadow-2xl border border-white/10 px-3 py-2.5 flex items-center gap-2.5">
      <span className="h-9 w-9 rounded-xl bg-blaze-green/15 text-blaze-green flex items-center justify-center flex-shrink-0">
        <Headphones size={17} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-white text-sm font-bold leading-tight truncate">{name || t('comms.voice')}</div>
        <div className="text-white/45 text-[11px] flex items-center gap-1">
          <span className="h-1.5 w-1.5 rounded-full bg-blaze-green inline-block animate-pulse" />
          {t('comms.inRoom',{count:total})}
        </div>
      </div>
      <PillBtn
        active={micEnabled}
        onClick={() => controls?.toggleMic()}
        on={<Mic size={15} />}
        off={<MicOff size={15} />}
        title={t(micEnabled?'comms.muteMic':'comms.unmuteMic')}
      />
      <PillBtn
        active={!deafened}
        onClick={() => controls?.toggleDeafen()}
        on={<Headphones size={15} />}
        off={<HeadphoneOff size={15} />}
        title={t(deafened?'comms.unmuteAudio':'comms.muteAudio')}
      />
      <button
        type="button"
        onClick={() => controls?.expand()}
        title={t('comms.expand')}
        className="h-11 w-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors flex-shrink-0"
      >
        <Maximize2 size={15} />
      </button>
      <button
        type="button"
        onClick={() => controls?.leave()}
        title={t('comms.leave')}
        className="h-11 w-11 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center transition-colors flex-shrink-0"
      >
        <PhoneOff size={15} />
      </button>
    </div>
  );
}

/**
 * Two bolitas beside the closed launcher: quick mute toggle + an "in voice"
 * indicator (click → expand the room). Only shown while connected + minimized.
 */
function VoiceBolitas({
  name,
  total,
  micEnabled,
  controls,
}: {
  name?: string;
  total: number;
  micEnabled: boolean;
  controls: VoiceRoomControls | null;
}) {
  const {t}=useTranslation();
  return (
    <div className="flex items-center gap-1.5">
      <button
        type="button"
        onClick={() => controls?.toggleMic()}
        title={t(micEnabled?'comms.muteMic':'comms.unmuteMic')}
        className={cn(
          'h-11 w-11 rounded-full shadow-lg flex items-center justify-center text-white transition-colors',
          micEnabled ? 'bg-ink-900/95 hover:bg-ink-900' : 'bg-red-500 hover:bg-red-600',
        )}
      >
        {micEnabled ? <Mic size={17} /> : <MicOff size={17} />}
      </button>
      <button
        type="button"
        onClick={() => controls?.expand()}
        title={t('comms.inVoice',{name:name||t('comms.voice'),count:total})}
        className="relative h-11 w-11 rounded-full bg-blaze-green shadow-lg flex items-center justify-center text-white hover:opacity-90 transition-opacity"
      >
        <Headphones size={17} />
        <span className="absolute inset-0 rounded-full ring-2 ring-blaze-green/50 animate-ping" />
      </button>
    </div>
  );
}

function PillBtn({
  active,
  onClick,
  on,
  off,
  title,
}: {
  active: boolean;
  onClick: () => void;
  on: React.ReactNode;
  off: React.ReactNode;
  title: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={cn(
        'h-11 w-11 rounded-full flex items-center justify-center transition-colors flex-shrink-0',
        active ? 'bg-white/10 hover:bg-white/20 text-white' : 'bg-red-500 hover:bg-red-600 text-white',
      )}
    >
      {active ? on : off}
    </button>
  );
}

function HubPanel({
  channels,
  channelState,
  onRetryChannels,
  tab,
  onTab,
  totalUnread,
  onClose,
  onPickChat,
  onJoinVoice,
  onCallStarted,
  showOpenFullChat,
  onOpenFullChat,
}: {
  channels: Channel[];
  channelState:'loading'|'error'|'ready';
  onRetryChannels:()=>void;
  tab: HubTab;
  onTab: (t: HubTab) => void;
  totalUnread: number;
  onClose: () => void;
  onPickChat: (id: string) => void;
  onJoinVoice: (c: Channel) => void;
  onCallStarted: () => void;
  showOpenFullChat: boolean;
  onOpenFullChat: () => void;
}) {
  const {t}=useTranslation();
  const panelId=React.useId();
  const [search, setSearch] = React.useState('');
  const [dmError,setDmError]=React.useState<unknown>(null);
  const mounted = React.useRef(true);
  React.useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const activeVoiceId = useVoiceSessionStore((s) => s.channelId);
  const voiceLive=useVoiceRoomStore(s=>s.connectionState==='live');
  const currentUserId = useAuthStore((s) => s.user?.id ?? null);
  const directoryQuery = useQuery(teamChatQueries.directory());
  const directoryData=directoryQuery.data;
  const createChannel = useCreateChannel();

  const directoryUsers = React.useMemo<DirectoryUser[]>(
    () => directoryData?.departments?.flatMap((d) => d.users ?? []) ?? [],
    [directoryData],
  );
  const directoryMap = React.useMemo(
    () => new Map(directoryUsers.map((u) => [u.id, u.name])),
    [directoryUsers],
  );

  const q = search.trim().toLowerCase();
  const match = (c: Channel) => !q || (c.name ?? '').toLowerCase().includes(q);
  // DMs: never match/label by the stored channel.name (it's fixed by the
  // CREATOR, so recipients would see their OWN name) — use the counterpart's
  // display name instead (TC-11).
  const dmLabel = (c: Channel) => dmDisplayName(c, currentUserId, directoryMap);
  const matchDm = (c: Channel) => !q || dmLabel(c).toLowerCase().includes(q);

  // TC-34: archived channels never surface in the hub (`!c.is_archived` is
  // defensive — the default GET /channels already hides them server-side; the
  // dedicated "Archivados" section lives in TeamChatPage only).
  const rooms = channels
    .filter((c) => c.type !== 'direct' && c.type !== 'voice' && !c.is_archived)
    .filter(match)
    .sort(compareHubChannels);
  const dms = channels
    .filter((c) => c.type === 'direct' && !c.is_archived)
    .filter(matchDm)
    .sort(compareHubChannels);
  const voiceChannels = React.useMemo(
    () => channels.filter((c) => c.type === 'voice' && !c.is_archived),
    [channels],
  );

  // TC-22: quiet channel = effective notify pref 'none' (server mute on my
  // members[] row, merged with the local 3-state pref).
  const isMuted = (c: Channel) =>
    effectiveNotifyPref(c, getChannelNotifyPref(c.id), currentUserId) === 'none';
  const voice = voiceChannels.filter(match);

  // TC-27: hydrate cross-room voice occupancy while the panel is open. The
  // queryFn's side effect populates the voice-presence store, so the "N en la
  // voz" badges are correct even if the user never visited Team Chat (which
  // mounts the same queries — shared queryKey dedupes the fetches). Unfiltered
  // list on purpose: badges must be warm before/regardless of a search.
  useQueries({
    queries: voiceChannels.map((c) => voiceQueries.participants(c.id)),
  });

  // "Personas" (TC-12): with an active search, offer directory users that match
  // but do NOT already have a DM listed above (and never myself). Clicking one
  // creates/reuses the DM (backend dedupes server-side, returning the existing
  // direct channel) and opens its dock window via the same onPickChat path.
  const dmUserIds = React.useMemo(() => {
    const ids = new Set<string>();
    for (const c of channels) {
      if (c.type !== 'direct') continue;
      const cp = dmCounterpart(c, currentUserId);
      if (cp) ids.add(cp.user_id);
    }
    return ids;
  }, [channels, currentUserId]);

  const people = q
    ? directoryUsers.filter(
        (u) =>
          u.id !== currentUserId &&
          !dmUserIds.has(u.id) &&
          (u.name ?? '').toLowerCase().includes(q),
      )
    : [];

  const startDm = async (user: DirectoryUser) => {
    if (createChannel.isPending) return;
    const revision=sessionRevision();setDmError(null);
    try {
      const created = await createChannel.mutateAsync({
        type: 'direct',
        name: user.name,
        member_ids: [user.id],
      });
      requireCurrentSession(revision);
      if (!mounted.current) return;
      if (!created?.id)throw Error('unconfirmed DM');
      onPickChat(created.id);
    } catch (err) {
      if(mounted.current && revision===sessionRevision())setDmError(err);
      void err;
    }
  };

  const changeTab = (next: HubTab) => {
    setSearch('');
    onTab(next);
  };

  return (
    <div role="dialog" aria-label={t('comms.title')} onKeyDown={event=>{if(event.key==='Escape'){event.preventDefault();event.stopPropagation();onClose();}}} className="mb-3 flex max-h-[min(40rem,calc(100dvh-6.5rem))] w-[min(24rem,calc(100vw-2rem))] flex-col overflow-hidden rounded-lg border border-white/70 bg-white/95 shadow-[0_28px_80px_rgba(28,20,56,0.24)] backdrop-blur-xl">
      {/* Modern command-center header: identity, state and full-view escape. */}
      <div className="relative overflow-hidden bg-ink-900 px-4 pb-3.5 pt-4 text-white">
        <span className="pointer-events-none absolute -end-14 -top-20 h-40 w-40 rounded-full bg-blaze-fuchsia/25 blur-3xl" />
        <span className="pointer-events-none absolute -bottom-20 -start-10 h-36 w-36 rounded-full bg-blaze-violet/30 blur-3xl" />
        <div className="relative flex items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-sm bg-white/10 ring-1 ring-white/15">
            <MessageSquare size={19} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="font-display text-base font-extrabold">{t('comms.title')}</h3>
              <span className="flex items-center gap-1 rounded-full bg-blaze-green/15 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide text-blaze-green">
                <span className="h-1.5 w-1.5 rounded-full bg-blaze-green" /> {t('comms.live')}
              </span>
            </div>
            <p className="mt-0.5 text-[11px] text-white/55">{t('comms.subtitle')}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('comms.closeHub')}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-sm text-white/60 transition-colors hover:bg-white/10 hover:text-white"
          >
            <X size={15} />
          </button>
        </div>
        <div className="relative mt-3 flex flex-wrap items-center justify-between gap-3">
          <span className="text-[10px] font-medium text-white/50">
            {totalUnread > 0
              ? t('comms.pendingMessages',{count:totalUnread})
              : t('comms.caughtUp')}
          </span>
          {showOpenFullChat && (
            <button
              type="button"
              onClick={onOpenFullChat}
              className="flex min-h-11 items-center gap-1.5 text-[10px] font-bold text-white/75 transition-colors hover:text-white"
            >
              {t('comms.openInternal')} <ArrowUpRight size={12} />
            </button>
          )}
        </div>
      </div>

      {!!dmError&&!createChannel.blocked&&<p role="status" className="px-3 py-2 text-sm text-blaze-magenta">{localizedErrorMessage(dmError,'common','comms.dmFailed')}</p>}
      <ChatCreationStatus/>
      {/* Tabs */}
      <div role="tablist" aria-label={t('comms.modes')} onKeyDown={event=>{
        if((event.target as HTMLElement).getAttribute('role')!=='tab')return;
        const keys=['ArrowLeft','ArrowRight','Home','End'];if(!keys.includes(event.key))return;
        event.preventDefault();const tabs=[...event.currentTarget.querySelectorAll<HTMLButtonElement>('[role=tab]')],current=tabs.indexOf(event.target as HTMLButtonElement);
        const right=document.documentElement.dir==='rtl'?-1:1;
        const next=event.key==='Home'?0:event.key==='End'?2:(current+(event.key==='ArrowRight'?right:-right)+3)%3;
        changeTab((['chat','voice','calls'] as HubTab[])[next]);tabs[next]?.focus();
      }} className="grid grid-cols-3 gap-1.5 border-b border-border-subtle bg-paper/55 p-2">
        <HubTabButton
          icon={<MessageSquare size={14} />}
          active={tab === 'chat'}
          id={`${panelId}-chat`} controls={`${panelId}-content`}
          badge={totalUnread}
          onClick={() => changeTab('chat')}
        >
          {t('comms.chat')}
        </HubTabButton>
        <HubTabButton id={`${panelId}-voice`} controls={`${panelId}-content`} icon={<Volume2 size={14} />} active={tab === 'voice'} onClick={() => changeTab('voice')}>
          {t('comms.voice')}
        </HubTabButton>
        <HubTabButton id={`${panelId}-calls`} controls={`${panelId}-content`} icon={<Phone size={14} />} active={tab === 'calls'} onClick={() => changeTab('calls')}>
          {t('comms.calls')}
        </HubTabButton>
      </div>

      {/* One contextual search surface for chat, voice and calls. */}
      <div className="border-b border-border-subtle px-3 py-2.5">
        <div className="relative">
          <Search size={14} className="pointer-events-none absolute start-3 top-1/2 -translate-y-1/2 text-fg-3" />
          <input
            autoFocus
            aria-label={t(tab==='chat'?'comms.searchChat':tab==='voice'?'comms.searchVoice':'comms.searchCalls')}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={
              tab === 'chat'
                ? t('comms.searchChat')
                : tab === 'voice'
                  ? t('comms.searchVoice')
                  : t('comms.searchCalls')
            }
            className="w-full rounded-sm border border-border-subtle bg-paper/60 py-2 ps-9 pe-3 text-sm text-fg-1 outline-none transition-all placeholder:text-fg-3 focus:border-blaze-violet focus:bg-white focus:ring-2 focus:ring-blaze-violet/10"
          />
        </div>
      </div>

      <div role="tabpanel" id={`${panelId}-content`} aria-labelledby={`${panelId}-${tab}`} className="min-h-0 flex-1 overflow-y-auto py-1.5">
        {tab!=='calls'&&channelState==='loading'&&<p role="status" className="px-3 py-3 text-sm">{t('loading')}</p>}
        {tab!=='calls'&&channelState==='error'&&<p role="status" className="px-3 py-3 text-sm">{t('comms.channelsFailed')} <button type="button" className="min-h-11 min-w-11" onClick={onRetryChannels}>{t('retry')}</button></p>}
        {tab==='chat'&&directoryQuery.isError&&<p role="status" className="px-3 py-3 text-sm">{t('comms.directoryFailed')} <button type="button" className="min-h-11 min-w-11" onClick={()=>void directoryQuery.refetch()}>{t('retry')}</button></p>}
        {tab === 'chat' && (
          <>
            {channelState==='ready'&&!directoryQuery.isError&&!directoryQuery.isPending&&rooms.length === 0 && dms.length === 0 && people.length === 0 && (
              <HubEmpty icon={<Search size={20} />} title={t('comms.noResults')} body={t('comms.searchChatHelp')} />
            )}
            {rooms.length > 0 && (
              <>
                <SectionLabel count={rooms.length}>{t('comms.channels')}</SectionLabel>
                {rooms.map((c) => (
                  <HubRow
                    key={c.id}
                    icon={<Hash size={14} />}
                    name={c.name}
                    subtitle={channelPreview(c)}
                    meta={formatCommsTime(c.last_message_at)}
                    unread={c.unread_count}
                    muted={isMuted(c)}
                    onClick={() => onPickChat(c.id)}
                  />
                ))}
              </>
            )}
            {dms.length > 0 && (
              <>
                <SectionLabel count={dms.length}>{t('comms.directs')}</SectionLabel>
                {dms.map((c) => {
                  const label = dmLabel(c);
                  const counterpart = dmCounterpart(c, currentUserId);
                  return (
                    <HubRow
                      key={c.id}
                      icon={
                        <HubDmAvatar
                          name={label}
                          avatar={counterpart?.user_avatar}
                          userId={counterpart?.user_id}
                        />
                      }
                      name={label}
                      subtitle={channelPreview(c, t('comms.direct'))}
                      meta={formatCommsTime(c.last_message_at)}
                      unread={c.unread_count}
                      muted={isMuted(c)}
                      onClick={() => onPickChat(c.id)}
                    />
                  );
                })}
              </>
            )}
            {people.length > 0 && (
              <>
                <SectionLabel count={people.length}>{t('comms.startConversation')}</SectionLabel>
                {people.map((u) => (
                  <HubRow
                    key={u.id}
                    icon={<HubDmAvatar name={u.name} avatar={u.avatar} userId={u.id} />}
                    name={u.name}
                    subtitle={t('comms.createDirect')}
                    onClick={() => void startDm(u)}
                  />
                ))}
              </>
            )}
          </>
        )}

        {tab === 'voice' && (
          <>
            {voice.length === 0 ? channelState==='ready'&&(
              <HubEmpty
                icon={<Volume2 size={20} />}
                title={t(q?'comms.noResults':'comms.noVoice')}
                body={t(q?'comms.searchNameHelp':'comms.noVoiceHelp')}
              />
            ) : (
              <>
                <SectionLabel count={voice.length}>{t('comms.availableRooms')}</SectionLabel>
                {voice.map((c) => (
                  <VoiceHubRow
                    key={c.id}
                    channel={c}
                    connected={activeVoiceId === c.id && voiceLive}
                    onClick={() => onJoinVoice(c)}
                  />
                ))}
              </>
            )}
          </>
        )}

        {tab === 'calls' && <HubCallsPanel query={q} onStarted={onCallStarted} />}
      </div>
    </div>
  );
}

function HubTabButton({
  id,
  controls,
  icon,
  active,
  badge,
  onClick,
  children,
}: {
  id:string;
  controls:string;
  icon: React.ReactNode;
  active: boolean;
  badge?: number;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      id={id} aria-controls={controls} tabIndex={active?0:-1}
      aria-selected={active}
      onClick={onClick}
      className={cn(
        'flex min-h-11 min-w-0 items-center justify-center gap-1.5 rounded-sm px-2 py-2 text-xs font-bold transition-all',
        active
          ? 'bg-white text-blaze-violet shadow-sm ring-1 ring-border-subtle'
          : 'text-fg-3 hover:bg-white/70 hover:text-fg-1',
      )}
    >
      {icon}
      {children}
      {!!badge && (
        <span
          className={cn(
            'flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[9px] font-extrabold',
            active ? 'bg-blaze-orange text-white' : 'bg-blaze-orange/10 text-blaze-orange',
          )}
        >
          {cappedCount(badge)}
        </span>
      )}
    </button>
  );
}

/**
 * Small round avatar for DM/person rows in the chat tab — counterpart photo
 * when available, otherwise initials derived from the DISPLAY name (i.e., the
 * counterpart's name, never the stored channel.name).
 */
function HubDmAvatar({
  name,
  avatar,
  userId,
}: {
  name: string;
  avatar?: string;
  userId?: string;
}) {
  const {t}=useTranslation();
  const presence = usePresence(userId);
  return (
    <span
      className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-sm text-[11px] font-bold text-white"
      style={{ background: avatar ? undefined : colorFromName(name) }}
    >
      {avatar ? (
        <img src={avatar} alt="" className="h-full w-full rounded-sm object-cover" />
      ) : (
        initialsFromName(name)
      )}
      <span
        aria-label={t(presence==='online'?'comms.online':'comms.offline')}
        className={cn(
          'absolute -bottom-0.5 -end-0.5 h-2.5 w-2.5 rounded-full border-2 border-white',
          presence === 'online' ? 'bg-blaze-green' : 'bg-fg-3',
        )}
      />
    </span>
  );
}

function compareHubChannels(a: Channel, b: Channel): number {
  const unreadDelta = (b.unread_count ?? 0) - (a.unread_count ?? 0);
  if (unreadDelta !== 0) return unreadDelta;
  const aTime = a.last_message_at ? Date.parse(a.last_message_at) : 0;
  const bTime = b.last_message_at ? Date.parse(b.last_message_at) : 0;
  if (aTime !== bTime) return bTime - aTime;
  return a.name.localeCompare(b.name);
}

function channelPreview(channel: Channel, fallback = commsText('teamChannel')): string {
  const preview = channel.last_message_preview;
  if (!preview) return fallback;
  const sender = channel.last_message_sender;
  return sender ? `${sender}: ${preview}` : preview;
}

function SectionLabel({ children, count }: { children: React.ReactNode; count?: number }) {
  return (
    <div className="flex items-center justify-between px-3.5 pb-1.5 pt-2.5 text-[10px] font-bold uppercase tracking-[0.12em] text-fg-3">
      <span>{children}</span>
      {count != null && <span className="font-mono text-[9px] tracking-normal text-fg-3/70">{formatNumber(count)}</span>}
    </div>
  );
}

function HubRow({
  icon,
  name,
  subtitle,
  meta,
  unread,
  muted,
  onClick,
}: {
  icon: React.ReactNode;
  name: string;
  subtitle?: string;
  meta?: string;
  unread?: number;
  /** TC-22: bell-off indicator + suppressed bold/badge highlight. */
  muted?: boolean;
  onClick: () => void;
}) {
  const {t}=useTranslation();
  return (
    <button
      type="button"
      onClick={onClick}
      className="group mx-1.5 flex w-[calc(100%-0.75rem)] items-center gap-2.5 rounded-sm px-2 py-2 text-start transition-colors hover:bg-paper"
    >
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-sm bg-blaze-violet/10 text-blaze-violet">
        {icon}
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-1.5">
          <span
            className="min-w-0 flex-1 truncate text-sm text-fg-1"
            style={{ fontWeight: !muted && (unread ?? 0) > 0 ? 750 : 650 }}
          >
            <bdi dir="auto">{name}</bdi>
          </span>
          {meta && <span className="max-w-[45%] break-words text-[9px] text-fg-3">{meta}</span>}
        </span>
        <span className="mt-0.5 flex items-center gap-1.5">
          <span className="min-w-0 flex-1 truncate text-[10px] text-fg-3">
            <bdi dir="auto">{subtitle || t('comms.noActivity')}</bdi>
          </span>
          {muted && <BellOff size={10} aria-label={t('comms.muted')} className="shrink-0 text-fg-3" />}
        </span>
      </span>
      {(unread ?? 0) > 0 ? (
        <span
          className={cn(
            'flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full px-1.5 text-[9px] font-extrabold text-white',
            muted ? 'bg-fg-3/70' : 'bg-blaze-orange',
          )}
        >
          {cappedCount(unread??0)}
        </span>
      ) : (
        <ChevronRight size={14} className="shrink-0 text-fg-3/40 transition-transform group-hover:translate-x-0.5 rtl:-scale-x-100 rtl:group-hover:-translate-x-0.5 group-hover:text-fg-2" />
      )}
    </button>
  );
}

function HubCallsPanel({ query, onStarted }: { query: string; onStarted: () => void }) {
  const {t}=useTranslation();
  const currentUserId = useAuthStore((state) => state.user?.id);
  const agentsQuery=useQuery(callQueries.agents());
  const {data:agents,isLoading}=agentsQuery;
  const startCall = useStartCall();
  const [callType, setCallType] = React.useState<'voice' | 'video'>('voice');
  const [selectedIds, setSelectedIds] = React.useState<Set<string>>(new Set());
  const [error, setError] = React.useState<string | null>(null);
  const mounted = React.useRef(true);
  React.useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

  const filteredAgents = React.useMemo(() => {
    const list = (agents ?? []).filter((agent) => agent.id && agent.id !== currentUserId);
    const matched = query
      ? list.filter((agent) =>
          `${agent.name ?? ''} ${agent.email ?? ''}`.toLowerCase().includes(query),
        )
      : list;
    return [...matched].sort((a, b) => {
      if (!!a.online !== !!b.online) return a.online ? -1 : 1;
      return (a.name || a.email || '').localeCompare(b.name || b.email || '');
    });
  }, [agents, currentUserId, query]);

  const toggleAgent = (id: string) => {
    setError(null);
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const start = () => {
    const ids = [...selectedIds];
    if (ids.length === 0 || startCall.isPending) return;
    const revision = sessionRevision();
    setError(null);
    const args = ids.length === 1
      ? { calleeId: ids[0], type: callType }
      : { calleeIds: ids, type: callType };
    startCall.mutate(args, {
      onSuccess: () => { if (mounted.current && revision === sessionRevision()) onStarted(); },
      onError: () => { if (mounted.current && revision === sessionRevision()) setError('comms.callFailed'); },
    });
  };

  return (
    <div className="flex min-h-full flex-col">
      <div className="px-3 pb-2 pt-1.5">
        <div className="grid grid-cols-2 gap-2 rounded-sm bg-paper p-1">
          <CallTypeButton
            active={callType === 'voice'}
            icon={<Phone size={13} />}
            onClick={() => setCallType('voice')}
          >
            {t('comms.voice')}
          </CallTypeButton>
          <CallTypeButton
            active={callType === 'video'}
            icon={<Video size={13} />}
            onClick={() => setCallType('video')}
          >
            {t('comms.video')}
          </CallTypeButton>
        </div>
        <div className="mt-2 flex items-center justify-between gap-3 px-0.5">
          <p className="text-[10px] leading-relaxed text-fg-3">
            {t('comms.callHelp')}
          </p>
          {selectedIds.size > 0 && (
            <span className="flex shrink-0 items-center gap-1 rounded-full bg-blaze-violet/10 px-2 py-1 text-[9px] font-bold text-blaze-violet">
              <Users size={10} /> {formatNumber(selectedIds.size)}
            </span>
          )}
        </div>
      </div>

      <div className="flex-1 pb-2">
        {isLoading ? (
          <div className="flex justify-center py-10">
            <Loader2 size={20} className="animate-spin text-blaze-orange" />
          </div>
        ) : agentsQuery.isError ? <p role="status" className="px-3 py-3 text-sm">{t('comms.agentsFailed')} <button type="button" className="min-h-11 min-w-11" onClick={()=>void agentsQuery.refetch()}>{t('retry')}</button></p> : filteredAgents.length === 0 ? (
          <HubEmpty
            icon={<Phone size={20} />}
            title={t('comms.noPeople')}
            body={t(query?'comms.searchPeopleHelp':'comms.noPeopleHelp')}
          />
        ) : (
          filteredAgents.map((agent) => (
            <CallAgentRow
              key={agent.id}
              agent={agent}
              selected={selectedIds.has(agent.id)}
              onClick={() => toggleAgent(agent.id)}
            />
          ))
        )}
      </div>

      <div className="sticky bottom-0 border-t border-border-subtle bg-white/95 p-3 backdrop-blur">
        {error && <p className="mb-2 text-[10px] font-semibold text-blaze-magenta">{t(error)}</p>}
        <button
          type="button"
          onClick={start}
          disabled={selectedIds.size === 0 || startCall.isPending}
          className="flex min-h-11 w-full items-center justify-center gap-2 rounded-sm bg-blaze-orange px-3 py-2.5 text-sm font-bold text-white shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md disabled:translate-y-0 disabled:cursor-not-allowed disabled:opacity-40 disabled:shadow-none"
        >
          {startCall.isPending ? (
            <Loader2 size={15} className="animate-spin" />
          ) : callType === 'video' ? (
            <Video size={15} />
          ) : (
            <Phone size={15} />
          )}
          {selectedIds.size > 1
            ? t('comms.callPeople',{count:selectedIds.size})
            : selectedIds.size === 1
              ? t('comms.startCall')
              : t('comms.selectCallee')}
        </button>
      </div>
    </div>
  );
}

function CallTypeButton({
  active,
  icon,
  onClick,
  children,
}: {
  active: boolean;
  icon: React.ReactNode;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        'flex items-center justify-center gap-1.5 min-h-11 rounded-xs py-1.5 text-xs font-bold transition-all',
        active ? 'bg-white text-blaze-orange shadow-sm' : 'text-fg-3 hover:text-fg-1',
      )}
    >
      {icon}
      {children}
    </button>
  );
}

function CallAgentRow({
  agent,
  selected,
  onClick,
}: {
  agent: AgentSummary;
  selected: boolean;
  onClick: () => void;
}) {
  const {t}=useTranslation();
  const presence = usePresence(agent.id);
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      className={cn(
        'mx-1.5 flex w-[calc(100%-0.75rem)] items-center gap-2.5 rounded-sm px-2 py-2 text-start transition-all',
        selected ? 'bg-blaze-orange/5 ring-1 ring-inset ring-blaze-orange/35' : 'hover:bg-paper',
      )}
    >
      <Avatar
        src={agent.avatar_url}
        fallback={agent.name || agent.email || '?'}
        size="sm"
        status={presence}
      />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-bold text-fg-1"><bdi dir="auto">{agent.name || t('comms.unnamed')}</bdi></span>
        <span className="mt-0.5 block truncate text-[10px] text-fg-3">
          <bdi dir="auto">{presence === 'online' ? t('comms.availableNow') : agent.email || t('comms.offline')}</bdi>
        </span>
      </span>
      <span
        className={cn(
          'flex h-6 w-6 shrink-0 items-center justify-center rounded-full border transition-all',
          selected
            ? 'border-blaze-orange bg-blaze-orange text-white'
            : 'border-border-subtle text-transparent',
        )}
      >
        <Check size={13} />
      </span>
    </button>
  );
}

function HubEmpty({
  icon,
  title,
  body,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
}) {
  return (
    <div className="flex flex-col items-center px-6 py-10 text-center">
      <span className="mb-3 flex h-11 w-11 items-center justify-center rounded-sm bg-paper text-fg-3">
        {icon}
      </span>
      <p className="text-xs font-bold text-fg-1">{title}</p>
      <p className="mt-1 text-[10px] leading-relaxed text-fg-3">{body}</p>
    </div>
  );
}

function VoiceHubRow({
  channel,
  connected,
  onClick,
}: {
  channel: Channel;
  connected: boolean;
  onClick: () => void;
}) {
  const {t}=useTranslation();
  const occupancy = useVoiceOccupancy(channel.id);
  const observation = useVoiceObservation(channel.id);
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'group mx-1.5 flex w-[calc(100%-0.75rem)] items-center gap-2.5 rounded-sm px-2 py-2.5 text-start transition-colors',
        connected ? 'bg-blaze-green/10' : 'hover:bg-paper',
      )}
      title={connected?t('comms.connected'):t('comms.joinVoice',{name:channel.name})}
    >
      <span
        className={cn(
          'flex h-9 w-9 shrink-0 items-center justify-center rounded-sm',
          connected || occupancy > 0 ? 'bg-blaze-green/12 text-blaze-green' : 'bg-paper text-fg-3',
        )}
      >
        <Volume2 size={15} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-bold text-fg-1"><bdi dir="auto">{channel.name}</bdi></span>
        <span className="mt-0.5 block text-[10px] text-fg-3">
          {connected
            ? t('comms.youConnected')
            : observation?.phase==='error' ? t('voice.presence_failed')
            : !observation?.value?.observedAt ? t('voice.no_observation')
            : occupancy > 0 ? t('voice.last_observation',{count:occupancy}) : t('comms.availableRoom')}
        </span>
      </span>
      <span
        className={cn(
          'rounded-full px-2 py-1 text-[9px] font-bold',
          connected
            ? 'bg-blaze-green text-white'
            : 'bg-blaze-violet/10 text-blaze-violet group-hover:bg-blaze-violet group-hover:text-white',
        )}
      >
        {t(connected?'comms.open':'comms.join')}
      </span>
    </button>
  );
}
